import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from 'react-modal';
import { useAuthentication } from '@connect-x-app/hybrid-state';
import { AdminConfigPermissions, SystemMessageConfiguration } from '@connect-x-app/shared-domain-models';
import { SubmitSystemMessageForm, SystemMessageWithType } from '@connect-x-app/shared-view-models';
import { ADMIN_TEST_IDS } from '@connect-x-app/web-test-ids';
import { Spinner } from '@connect-x-app/web-ui';
import { Row } from '@tanstack/react-table';

import { EmptyState } from '../../../../shared';
import { customModalStyles } from '../../../../shared/modal/ModalUtils';
import { EditSystemMessageModal } from './components/editSystemMessageModal/EditSystemMessageModal';
import { SystemMessageTableGroup } from './components/systemMessageTableGroup/SystemMessageTableGroup';
import { useGroupedSystemMessages } from './hooks/useGroupedSystemMessages';

export const SystemMessages: FC = () => {
  const { t } = useTranslation();

  const { hasPermission } = useAuthentication();

  const [editSystemMessage, setEditSystemMessage] = useState<SystemMessageWithType>();

  const { groupedMessages, isLoadingGetSystemMessages, isLoadingUpdateSystemMessages, systemMessages, updateSystemMessages } =
    useGroupedSystemMessages();

  const isValidData = (data: SystemMessageWithType[]) => data?.length > 0;

  const hasUpdatePermission = useMemo(() => {
    return hasPermission(AdminConfigPermissions.WRITE);
  }, [hasPermission]);

  const onRowClicked = (row: Row<SystemMessageWithType>) => {
    setEditSystemMessage(row.original);
  };

  const closeEditSystemMessagesModal = () => {
    setEditSystemMessage(undefined);
  };

  const validGroupedMessages = useMemo(() => {
    return Object.entries(groupedMessages).filter(([_title, data]) => isValidData(data));
  }, [groupedMessages]);

  if (isLoadingGetSystemMessages) {
    return <Spinner>{t('SHARED.LOADING_APPLICATION')}</Spinner>;
  }

  if (validGroupedMessages?.length <= 0) {
    return (
      <EmptyState description={t('SYSTEM_MESSAGES.EMPTY_STATE.DESCRIPTION')} title={t('SYSTEM_MESSAGES.EMPTY_STATE.TITLE')} />
    );
  }

  return (
    <>
      <Modal isOpen={!!editSystemMessage} onRequestClose={closeEditSystemMessagesModal} style={customModalStyles}>
        {editSystemMessage ? (
          <EditSystemMessageModal
            closeModal={closeEditSystemMessagesModal}
            isLoading={isLoadingUpdateSystemMessages}
            onSubmit={async (editedSystemMessage: SubmitSystemMessageForm | null) => {
              if (editedSystemMessage && systemMessages) {
                const editedSystemMessageConfiguration: SystemMessageConfiguration = {
                  ...systemMessages,
                  [editedSystemMessage.messageType]: editedSystemMessage.data,
                };

                await updateSystemMessages(editedSystemMessageConfiguration);
              }

              closeEditSystemMessagesModal();
            }}
            systemMessage={editSystemMessage}
          />
        ) : null}
      </Modal>

      <div data-test={ADMIN_TEST_IDS.systemMessages.tables}>
        {validGroupedMessages.map(([title, data]) => (
          <SystemMessageTableGroup
            data={data}
            key={title}
            onRowClicked={onRowClicked}
            title={title}
            updatePermission={hasUpdatePermission}
          />
        ))}
      </div>
    </>
  );
};
